/**
 * core/schema/seed.ts · 初始 seed 数据 (干净 IndexedDB 第一次跑)
 *
 * 复用 v200.16.1 employees 40 账号 + 业务示范数据:
 *   - 13 部门 · 40 账号 · PBKDF2 hash (R14 严守)
 *   - 业务示范 SO/customer/product/work_order 让 *Home KPI 有数显
 *
 * 团队 W2 任务:
 *   - 从 v200.16.1 生产 D1 真数据导出 → 替换本文件 (而非示范)
 *   - 当前是 dev/demo 数据 · 不能上线 production (版本见 src/constants/company.ts APP_VERSION)
 */

import type { Database } from 'sql.js';

const PBKDF2_ITER = 310000;
const SALT_BYTES = 16;
const HASH_BITS = 256;

/**
 * 13 部门 40 账号 (Jack 已批准 · 复用 v200.16.1 employees 设计)
 *
 * **Demo 密码**: 全部用 'Trungnam2026!' 简化 (生产从 D1 导出真 hash + salt)
 * **R14 严守**: 仍 PBKDF2 SHA-256 310k iter · 16 字节独立 salt
 */
const ACCOUNTS = [
  { code: 'GM01', name: '朱杰', role: 'admin', sub_role: null, department: '管理', pwd: 'TN-GM-26' },
  { code: 'GM02', name: '副总经理', role: 'admin', sub_role: null, department: '管理', pwd: 'Trungnam2026!' },

  // sales 10
  { code: 'SALES-01', name: '销售经理 1', role: 'sales', sub_role: null, department: '销售部', pwd: 'Trungnam2026!' },
  { code: 'SALES-02', name: '销售经理 2', role: 'sales', sub_role: null, department: '销售部', pwd: 'Trungnam2026!' },
  { code: 'SALES-10', name: '销售助理', role: 'sales', sub_role: null, department: '销售部', pwd: 'Trungnam2026!' },

  // engineer 3
  { code: 'ENG-01', name: '工程主管', role: 'engineer', sub_role: null, department: '工程部', pwd: 'Trungnam2026!' },
  { code: 'ENG-02', name: '工艺工程师', role: 'engineer', sub_role: null, department: '工程部', pwd: 'Trungnam2026!' },

  // finance 2
  { code: 'FIN-01', name: '财务主管', role: 'finance', sub_role: null, department: '财务部', pwd: 'Trungnam2026!' },
  { code: 'FIN-02', name: '出纳会计', role: 'finance', sub_role: null, department: '财务部', pwd: 'Trungnam2026!' },

  // hr 2
  { code: 'HR-01', name: '人事主管', role: 'hr', sub_role: 'manager', department: '人事部', pwd: 'Trungnam2026!' },
  { code: 'HR-02', name: '工资专员', role: 'hr', sub_role: 'payroll', department: '人事部', pwd: 'Trungnam2026!' },

  // warehouse 5
  { code: 'WH-01', name: '仓储主管', role: 'warehouse', sub_role: null, department: '仓储部', pwd: 'Trungnam2026!' },
  { code: 'WH-02', name: '原料仓管', role: 'warehouse', sub_role: 'raw', department: '仓储部', pwd: 'Trungnam2026!' },
  { code: 'WH-03', name: '成品仓管', role: 'warehouse', sub_role: 'finished', department: '仓储部', pwd: 'Trungnam2026!' },
  { code: 'WH-05', name: '物流调度', role: 'warehouse', sub_role: 'logistics', department: '仓储部', pwd: 'Trungnam2026!' },

  // purchasing 3
  { code: 'PUR-01', name: '采购主管', role: 'purchasing', sub_role: null, department: '采购部', pwd: 'Trungnam2026!' },
  { code: 'PUR-02', name: '采购员-内贸', role: 'purchasing', sub_role: null, department: '采购部', pwd: 'Trungnam2026!' },

  // quality 3
  { code: 'QC-01', name: '品控主管', role: 'quality', sub_role: null, department: '品控部', pwd: 'Trungnam2026!' },
  { code: 'QC-02', name: 'IQC 进货检验', role: 'quality', sub_role: null, department: '品控部', pwd: 'Trungnam2026!' },

  // maintenance 3
  { code: 'MAINT-01', name: '维修主管', role: 'maintenance', sub_role: null, department: '维修部', pwd: 'Trungnam2026!' },
  { code: 'MAINT-02', name: '机修工程师', role: 'maintenance', sub_role: null, department: '维修部', pwd: 'Trungnam2026!' },

  // supervisor 3
  { code: 'SUP-01', name: '印刷班长', role: 'supervisor', sub_role: null, department: '生产部', pwd: 'Trungnam2026!' },
  { code: 'SUP-02', name: '模切班长', role: 'supervisor', sub_role: null, department: '生产部', pwd: 'Trungnam2026!' },

  // worker (示例)
  { code: 'W-01', name: '工人 1', role: 'worker', sub_role: null, department: '生产部', pwd: 'Trungnam2026!' },
  { code: 'W-02', name: '工人 2', role: 'worker', sub_role: null, department: '生产部', pwd: 'Trungnam2026!' },

  // guard
  { code: 'GUARD-01', name: '门卫', role: 'guard', sub_role: null, department: '行政', pwd: 'Trungnam2026!' },

  // iso
  { code: 'ISO-01', name: 'ISO 文档员', role: 'iso', sub_role: null, department: '质量管理', pwd: 'Trungnam2026!' },
];

/**
 * Seed 数据 · 第一次 init 调用
 */
export async function runSeed(db: Database): Promise<void> {
  // 1. 建 employees 表 + seed (R14 严守 PBKDF2)
  db.exec(`
    CREATE TABLE IF NOT EXISTS employees (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT UNIQUE NOT NULL,
      name TEXT NOT NULL,
      role TEXT NOT NULL,
      sub_role TEXT,
      department TEXT,
      salt TEXT NOT NULL,
      pwd_hash TEXT NOT NULL,
      status TEXT DEFAULT 'active',
      hire_date TEXT,
      deleted_at TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
  `);

  // 已 seed 跳过
  const existing = db.exec(`SELECT COUNT(*) as c FROM employees`);
  if (existing[0] && existing[0].values[0] && (existing[0].values[0][0] as number) > 0) {
    console.log('[seed] employees already seeded · ran migrations · skip seed');
    return;
  }

  for (const acc of ACCOUNTS) {
    const { salt, hash } = await hashPassword(acc.pwd);
    db.run(
      `INSERT INTO employees (code, name, role, sub_role, department, salt, pwd_hash, hire_date)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
      [acc.code, acc.name, acc.role, acc.sub_role, acc.department, salt, hash, '2024-01-01']
    );
  }
  console.log('[seed] employees seeded:', ACCOUNTS.length);

  // 2. 建业务示范表 (让 *Home KPI 有数显)
  db.exec(`
    CREATE TABLE IF NOT EXISTS customers (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT UNIQUE,
      name TEXT,
      contact TEXT,
      status TEXT DEFAULT 'active',
      deleted_at TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE TABLE IF NOT EXISTS sales_orders (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT UNIQUE,
      customer_id INTEGER,
      total_amount REAL,
      status TEXT,
      due_date TEXT,
      delivery_date TEXT,
      deleted_at TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE TABLE IF NOT EXISTS customer_payments (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      ref TEXT,
      customer_id INTEGER,
      amount REAL,
      payment_date TEXT,
      notes TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE TABLE IF NOT EXISTS purchase_orders (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT UNIQUE,
      supplier_id INTEGER,
      total_amount REAL,
      status TEXT,
      deleted_at TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE TABLE IF NOT EXISTS work_orders (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT UNIQUE,
      so_id INTEGER,
      product_id INTEGER,
      planned_qty INTEGER,
      completed_qty INTEGER,
      status TEXT,
      urgency TEXT DEFAULT 'medium',
      due_date TEXT,
      responsible_supervisor_id INTEGER,
      end_date TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE TABLE IF NOT EXISTS products (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT UNIQUE,
      name TEXT,
      deleted_at TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE TABLE IF NOT EXISTS bom_items (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      product_id INTEGER,
      material_id INTEGER,
      qty REAL,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE TABLE IF NOT EXISTS quotations (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT UNIQUE,
      customer_id INTEGER,
      product_description TEXT,
      qty INTEGER,
      unit_price REAL,
      total REAL,
      valid_until TEXT,
      notes TEXT,
      status TEXT,
      deleted_at TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE TABLE IF NOT EXISTS suppliers (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT UNIQUE,
      name TEXT,
      deleted_at TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE TABLE IF NOT EXISTS raw_materials (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT UNIQUE,
      name TEXT,
      stock_qty REAL DEFAULT 0,
      safety_stock REAL DEFAULT 0,
      reorder_point REAL,
      unit_price REAL,
      is_active INTEGER DEFAULT 1,
      deleted_at TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE TABLE IF NOT EXISTS auxiliary_materials (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT UNIQUE,
      name TEXT,
      stock_qty REAL DEFAULT 0,
      unit_price REAL,
      is_active INTEGER DEFAULT 1,
      deleted_at TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE TABLE IF NOT EXISTS inventory (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      product_id INTEGER,
      qty INTEGER,
      unit_price REAL,
      batch TEXT,
      inbound_date TEXT,
      status TEXT DEFAULT 'in_stock',
      wo_id INTEGER,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE TABLE IF NOT EXISTS material_receipts (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT,
      po_id INTEGER,
      supplier_id INTEGER,
      material_type TEXT,
      material_id INTEGER,
      qty REAL,
      qty_received REAL,
      receipt_date TEXT,
      iqc_status TEXT,
      notes TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE TABLE IF NOT EXISTS shipping_notes (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT UNIQUE,
      so_id INTEGER,
      customer_id INTEGER,
      vehicle_id INTEGER,
      driver_id INTEGER,
      ship_date TEXT,
      delivery_date TEXT,
      status TEXT DEFAULT 'planned',
      total_qty INTEGER,
      total_value REAL,
      oqc_status TEXT,
      signed_back INTEGER DEFAULT 0,
      notes TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE TABLE IF NOT EXISTS vehicles (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      vehicle_code TEXT UNIQUE,
      plate_number TEXT,
      vehicle_type TEXT,
      capacity_ton REAL,
      driver_id INTEGER,
      status TEXT DEFAULT 'available',
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE TABLE IF NOT EXISTS qc_records (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      qc_type TEXT,
      work_order_id INTEGER,
      receipt_id INTEGER,
      shipping_note_id INTEGER,
      process_id INTEGER,
      material_id INTEGER,
      product_id INTEGER,
      batch TEXT,
      inspector_id INTEGER,
      ect_value REAL,
      burst_value REAL,
      color_check TEXT,
      size_check TEXT,
      result TEXT,
      defect_rate REAL,
      remark TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE TABLE IF NOT EXISTS processes (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT UNIQUE,
      name_cn TEXT,
      name_vi TEXT,
      equipment_type TEXT,
      std_minutes REAL,
      cost_per_min REAL,
      seq_no INTEGER,
      deleted_at TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE TABLE IF NOT EXISTS work_reports (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      work_order_id INTEGER,
      worker_code TEXT,
      qty_good INTEGER,
      report_time TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE TABLE IF NOT EXISTS equipment (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT UNIQUE,
      name TEXT,
      status TEXT DEFAULT 'running',
      is_active INTEGER DEFAULT 1,
      department TEXT,
      model TEXT,
      specification TEXT,
      qty INTEGER DEFAULT 1,
      equipment_type TEXT,
      next_maint_date TEXT,
      next_maint_hours REAL,
      next_maint_qty INTEGER,
      last_maint_date TEXT,
      last_maint_qty INTEGER,
      pm_cycle_days INTEGER,
      deleted_at TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE TABLE IF NOT EXISTS maintenance_tasks (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      equipment_id INTEGER,
      status TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE TABLE IF NOT EXISTS purchase_requisitions (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT UNIQUE,
      material_description TEXT,
      qty REAL,
      urgency TEXT DEFAULT 'medium',
      reason TEXT,
      status TEXT DEFAULT 'pending',
      converted_po_id INTEGER,
      requester_id INTEGER,
      deleted_at TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE TABLE IF NOT EXISTS customer_invoices (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT UNIQUE,
      customer_id INTEGER,
      total REAL,
      paid_amount REAL DEFAULT 0,
      status TEXT,
      deleted_at TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE TABLE IF NOT EXISTS iso_documents (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT,
      title TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE TABLE IF NOT EXISTS guard_log (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      ref_type TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE TABLE IF NOT EXISTS guard_patrol_logs (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      point_id INTEGER,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
  `);

  // 3. 示范数据 (让 KPI 不全 0)
  const now = new Date().toISOString();
  // 5 客户
  for (let i = 1; i <= 5; i++) {
    db.run(`INSERT INTO customers (code, name, contact) VALUES (?, ?, ?)`,
      [`C${String(i).padStart(3, '0')}`, `示范客户 ${i}`, '0901234567']);
  }
  // 27 sales_orders (本月 · 加 due_date)
  for (let i = 1; i <= 27; i++) {
    const dueDate = new Date(Date.now() + (i * 2 + 5) * 86400000).toISOString().slice(0, 10);
    db.run(`INSERT INTO sales_orders (code, customer_id, total_amount, status, due_date, created_at) VALUES (?, ?, ?, ?, ?, ?)`,
      [`SO-2026-${String(i).padStart(4, '0')}`, (i % 5) + 1, 100000000 + i * 1000000,
        i < 10 ? 'confirmed' : i < 20 ? 'in_production' : 'shipped', dueDate, now]);
  }
  // 5 quotations (3 pending + 1 confirmed + 1 expired) · SALES 新人登录看 quotationList 不空
  const qtyArr = [500, 1000, 2000, 800, 1500];
  const priceArr = [120000, 95000, 85000, 110000, 100000];
  const statusArr = ['pending', 'pending', 'confirmed', 'pending', 'expired'];
  const descArr = [
    'K9 瓦楞纸箱 350×250×200 · 双印 4色',
    'C5 瓦楞箱 300×200×150 · 单印 2色',
    'BC 瓦楞 5 层 400×300×250 · 单印 1色',
    'K7 食品级纸箱 250×200×150 · UV印',
    'BE 飞机盒 200×150×50 · 双印',
  ];
  for (let i = 0; i < 5; i++) {
    const total = qtyArr[i] * priceArr[i];
    const validUntil = new Date(Date.now() + (i === 4 ? -5 : 30) * 86400000).toISOString().slice(0, 10);
    try {
      // 防御性 try/catch: 老 db 缺列时 fallback 到精简版 INSERT (不阻塞 bootstrap)
      db.run(`INSERT INTO quotations (code, customer_id, product_description, qty, unit_price, total, valid_until, status, created_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [`QUO-2026-${String(i + 1).padStart(4, '0')}`, (i % 5) + 1, descArr[i],
          qtyArr[i], priceArr[i], total, validUntil, statusArr[i], now]);
    } catch {
      try {
        db.run(`INSERT INTO quotations (code, customer_id, total, status, created_at) VALUES (?, ?, ?, ?, ?)`,
          [`QUO-2026-${String(i + 1).padStart(4, '0')}`, (i % 5) + 1, total, statusArr[i], now]);
      } catch {
        /* 重复 INSERT (re-seed) 跳过 */
      }
    }
  }
  // 2 customer_payments (本月 · 总额 1B)
  db.run(`INSERT INTO customer_payments (ref, customer_id, amount, payment_date) VALUES (?, ?, ?, ?)`,
    ['PAY-001', 1, 552000000, now]);
  db.run(`INSERT INTO customer_payments (ref, customer_id, amount, payment_date) VALUES (?, ?, ?, ?)`,
    ['PAY-002', 2, 448000000, now]);
  // 71 products
  for (let i = 1; i <= 71; i++) {
    db.run(`INSERT INTO products (code, name) VALUES (?, ?)`,
      [`P${String(i).padStart(4, '0')}`, `示范产品 ${i}`]);
  }
  // BOM items (covering 71 products)
  for (let i = 1; i <= 71; i++) {
    db.run(`INSERT INTO bom_items (product_id, material_id, qty) VALUES (?, ?, ?)`,
      [i, 1, 1.0]);
  }
  // 12 真实工艺路线 (按生产顺序 · 关联 equipment_type · v352 三级厂)
  const realProcs: Array<[string, string, string, string, number, number, number]> = [
    // [code, cn, vi, equipment_type, std_minutes, cost_per_min(₫), seq_no]
    ['PROC-01', '打稿',      'In bản nháp',        'prototype',   30,  1200, 1],
    ['PROC-02', '制版',      'Làm bản in',         'plate',       60,  1500, 2],
    ['PROC-03', '介样/刀模', 'Khuôn mẫu',          'prototype',   90,  1800, 3],
    ['PROC-04', '裱纸',      'Dán giấy',           'lamination',  10,  2000, 4],
    ['PROC-05', '印刷',      'In',                 'printing',    8,   3500, 5],
    ['PROC-06', '模切',      'Bế (Die-cut)',       'diecut',      15,  2500, 6],
    ['PROC-07', '粘合',      'Dán keo',            'gluing',      6,   1800, 7],
    ['PROC-08', '钉箱',      'Đóng đinh',          'nail',        5,   1500, 8],
    ['PROC-09', '分纸',      'Cắt giấy',           'lamination',  8,   1600, 9],
    ['PROC-10', '打角',      'Cắt góc',            'lamination',  8,   1600, 10],
    ['PROC-11', '贴标',      'Dán nhãn',           'label',       4,   1200, 11],
    ['PROC-12', 'QC 检验',   'Kiểm tra QC',        'lab',         15,  2000, 12],
  ];
  realProcs.forEach(([code, cn, vi, eqType, stdMin, cost, seq]) => {
    try {
      db.run(`INSERT INTO processes (code, name_cn, name_vi, equipment_type, std_minutes, cost_per_min, seq_no) VALUES (?, ?, ?, ?, ?, ?, ?)`,
        [code, cn, vi, eqType, stdMin, cost, seq]);
    } catch {
      try {
        db.run(`INSERT INTO processes (code, name_cn, name_vi) VALUES (?, ?, ?)`, [code, cn, vi]);
      } catch { /* re-seed skip */ }
    }
  });

  // 34 真实设备 (Trungnam 2026-04-09 清单 · 9 部门)
  // [code, name (model + 中文), department, equipment_type, specification, qty]
  const realEqs: Array<[string, string, string, string, string, number]> = [
    // 印刷机 Printing (8 台)
    ['F1-PSN7D1218',  '6C+1 全自动印刷机 (含模切/压痕)',  '印刷机', 'printing',  '1200×1800mm · 9000pcs/h',  1],
    ['F3-DF4S1628',   '4C 东方全自动印刷机',                '印刷机', 'printing',  '1600×2800mm · 10000pcs/h', 1],
    ['F4-DL4SD1224',  '4C 自动印刷机 (含模切/压痕)',       '印刷机', 'printing',  '1200×2400mm · 10800pcs/h', 1],
    ['F5-DS1D2229',   '1C 单色开槽模切印刷机',              '印刷机', 'printing',  '2200×2900mm · 1600pcs/h',  1],
    ['F6-HY2SD2232',  '2C 自动印刷机 (含模切/压痕)',       '印刷机', 'printing',  '2200×3200mm · 8000pcs/h',  1],
    ['KB2500-12',     '12 头数码印刷机',                    '印刷机', 'printing',  '2500mm · 1000m²/h',         1],
    ['KB2500-4',      '4 头数码印刷机',                     '印刷机', 'printing',  '2500mm · 300m²/h',          1],
    ['DIG1100-1',     'EPSON 爱普生打稿机',                 '印刷机', 'prototype', '1100mm',                    1],
    // 制版机 Plate (1 台 · qty=2)
    ['LASER100',      '激光雕版机',                         '制版机', 'plate',     '1300×1000mm',               2],
    // 介样机 Prototyping (1 台)
    ['CNC1625',       'CNC 自动介样机 (刀模)',              '介样机', 'prototype', '1600×2500mm',               1],
    // 模切机 Diecut (6 台)
    ['DIECUT1-1510',  '半自动模切机',                       '模切机', 'diecut',    '1500×1000mm · 3000pcs/h',   1],
    ['DIECUT2-2215',  '半自动模切机',                       '模切机', 'diecut',    '2200×1500mm · 3000pcs/h',   1],
    ['DIECUT3-1813',  '全自动模切机',                       '模切机', 'diecut',    '1800×1300mm · 1000pcs/h',   1],
    ['DIECUT4-2214',  '全自动模切机',                       '模切机', 'diecut',    '2200×1400mm · 800pcs/h',    1],
    ['DIECUT5-2516',  '全自动模切机',                       '模切机', 'diecut',    '2500×1600mm · 500pcs/h',    1],
    ['DIECUT6-1007',  '全自动模切机 (高速)',                '模切机', 'diecut',    '1020×720mm · 5000pcs/h',    1],
    // 打钉机 Nail (4 台)
    ['DONGDINH1-2400', '伺服双联半自动打钉机',              '打钉机', 'nail',      '2400mm · 1000pcs/h',        1],
    ['DONGDINH2-2800', '伺服双联半自动打钉机',              '打钉机', 'nail',      '2800mm · 1000pcs/h',        1],
    ['DONGDINH3-3000', '伺服双联半自动打钉机',              '打钉机', 'nail',      '3000mm · 1000pcs/h',        1],
    ['DONGDINHS1-2400','伺服单片半自动打钉机',              '打钉机', 'nail',      '2400mm · 1000pcs/h',        1],
    // 粘合机 Gluing (2 台)
    ['DANHO1-2400',   '全自动粘合机',                       '粘合机', 'gluing',    '2400mm · 70m/min',          1],
    ['DANDONGDINH1-2400', '半自动粘合机',                   '粘合机', 'gluing',    '2800mm · 70m/min',          1],
    // 裱纸机 Lamination (5 台)
    ['DANTEMTU01-2222','全自动裱纸机',                      '裱纸机', 'lamination','2200×2200mm · 9000pcs/h',   1],
    ['MayXa3000',     '分纸机',                             '裱纸机', 'lamination','3000mm · 2000pcs/h',        1],
    ['MayXa2500',     '分纸机',                             '裱纸机', 'lamination','2500mm · 2000pcs/h',        1],
    ['Maychap3000',   '打角机',                             '裱纸机', 'lamination','3000mm · 2000pcs/h',        1],
    ['Maychap2500',   '打角机',                             '裱纸机', 'lamination','2500mm · 2000pcs/h',        1],
    // 贴标机 Label (1 机型 · qty=14)
    ['DANTEMAY01-1200','贴标机',                            '贴标机', 'label',     '1200mm · 3000pcs/h',        14],
    // 实验室 Laboratory (6 台)
    ['BST1',          '耐破强度测试仪 (Bursting Test)',     '实验室', 'lab',       '-',                         1],
    ['ECT1',          '边压强度测试仪 (Edge Crush Test)',   '实验室', 'lab',       '-',                         1],
    ['RuberT1',       '油墨脱色测试仪 (Rub Test)',          '实验室', 'lab',       '-',                         1],
    ['X-Rite',        'X-Rite 分光密度仪',                  '实验室', 'lab',       '-',                         1],
    ['i1',            '爱色丽 i1 色彩扫描仪',               '实验室', 'lab',       '-',                         1],
    ['HeavyMetalDetector', '重金属检测仪',                  '实验室', 'lab',       '-',                         1],
  ];
  realEqs.forEach(([code, name, dept, eqType, spec, qty]) => {
    try {
      db.run(`INSERT INTO equipment (code, name, department, equipment_type, specification, qty, status) VALUES (?, ?, ?, ?, ?, ?, 'running')`,
        [code, name, dept, eqType, spec, qty]);
    } catch {
      try {
        db.run(`INSERT INTO equipment (code, name, status) VALUES (?, ?, 'running')`, [code, name]);
      } catch { /* re-seed skip */ }
    }
  });
  // 7 qc_records (今日合格)
  for (let i = 1; i <= 7; i++) {
    db.run(`INSERT INTO qc_records (work_order_id, result, created_at) VALUES (?, ?, ?)`,
      [i, '合格', now]);
  }

  // ⭐ Sprint 3 业务 demo 扩充 · 让 5 流程闭环可演示
  // 5 供应商
  const sups = ['Vietlong Paper', 'Trang Trung Industrial', 'Saigon Paper', 'An Binh Co.', 'Dong Tien Ltd.'];
  sups.forEach((name, i) => {
    db.run(`INSERT INTO suppliers (code, name) VALUES (?, ?)`,
      [`SUP-${String(i + 1).padStart(3, '0')}`, name]);
  });
  // 10 原料 (真纸种 K/A/C/HG/T 系列 · 含 unit_price + safety_stock)
  const rms = [
    { code: 'K230-1200', name: 'K纸 K230gsm × 1200mm', stock: 5000, safety: 2000, price: 18000 },
    { code: 'K175-1200', name: 'K纸 K175gsm × 1200mm', stock: 8000, safety: 3000, price: 14500 },
    { code: 'K150-1200', name: 'K纸 K150gsm × 1200mm', stock: 3000, safety: 1500, price: 12000 },
    { code: 'A175-1200', name: 'A纸 A175gsm × 1200mm', stock: 6500, safety: 2500, price: 13500 },
    { code: 'T230-1200', name: 'T纸 T230gsm × 1200mm', stock: 200, safety: 1000, price: 19500 }, // 低
    { code: 'C125-1200', name: 'C纸 C125gsm × 1200mm', stock: 4200, safety: 1500, price: 10500 },
    { code: 'C115-1200', name: 'C纸 C115gsm × 1200mm', stock: 7800, safety: 2500, price: 9500 },
    { code: 'HG175-1200', name: 'HG纸 HG175gsm × 1200mm', stock: 100, safety: 800, price: 16500 }, // 低
    { code: 'WT200-1200', name: 'WT纸 WT200gsm × 1200mm', stock: 1500, safety: 600, price: 15500 },
    { code: 'KB180-1200', name: 'KB纸 KB180gsm × 1200mm', stock: 50, safety: 500, price: 17000 }, // 低
  ];
  rms.forEach(rm => {
    db.run(`INSERT INTO raw_materials (code, name, stock_qty, safety_stock, reorder_point, unit_price) VALUES (?, ?, ?, ?, ?, ?)`,
      [rm.code, rm.name, rm.stock, rm.safety, rm.safety * 1.5, rm.price]);
  });
  // 5 辅料 (含 unit_price + stock)
  const auxes = [
    { name: '白胶 PVAc', stock: 800, price: 35000 },
    { name: '黑墨水 K', stock: 150, price: 280000 },
    { name: '蓝墨水 C', stock: 80, price: 280000 },
    { name: '钉子 16mm', stock: 50000, price: 50 },
    { name: '丝带 5mm', stock: 12000, price: 1200 },
  ];
  auxes.forEach((a, i) => {
    db.run(`INSERT INTO auxiliary_materials (code, name, stock_qty, unit_price) VALUES (?, ?, ?, ?)`,
      [`AUX-${String(i + 1).padStart(3, '0')}`, a.name, a.stock, a.price]);
  });
  // 5 PR (待转 PO)
  const prs = [
    { mat: 'K纸 K230gsm 1200mm 卷', qty: 2000, urgency: 'high', reason: 'WO-2026-001 急用 · 当前库存 5000 不够' },
    { mat: 'A纸 A175gsm 1200mm 卷', qty: 5000, urgency: 'medium', reason: '本月生产排期 · 安全库存补充' },
    { mat: '白胶 PVAc 200kg', qty: 200, urgency: 'low', reason: '辅料常规采购' },
    { mat: 'T纸 T230gsm 1200mm 卷', qty: 1500, urgency: 'urgent', reason: '🔥 紧急 · 库存仅 200 卷' },
    { mat: 'HG纸 HG175gsm 1200mm 卷', qty: 800, urgency: 'high', reason: '客户 SO-2026-XXX 专用' },
  ];
  prs.forEach((pr, i) => {
    db.run(`INSERT INTO purchase_requisitions (code, material_description, qty, urgency, reason, status) VALUES (?, ?, ?, ?, ?, ?)`,
      [`PR-2026-${String(i + 1).padStart(4, '0')}`, pr.mat, pr.qty, pr.urgency, pr.reason, 'pending']);
  });
  // 5 PO (3 approved + 1 partial + 1 draft) · PUR 新人登录 poList 不空
  const poData = [
    { sup: 1, total: 80000000, status: 'approved' },
    { sup: 2, total: 120000000, status: 'approved' },
    { sup: 3, total: 45000000, status: 'partial' },
    { sup: 1, total: 60000000, status: 'approved' },
    { sup: 2, total: 30000000, status: 'draft' },
  ];
  poData.forEach((po, i) => {
    db.run(`INSERT INTO purchase_orders (code, supplier_id, total_amount, status) VALUES (?, ?, ?, ?)`,
      [`PO-2026-${String(i + 1).padStart(4, '0')}`, po.sup, po.total, po.status]);
  });
  // 10 audit_events demo (5 login + 3 SO + 2 inventory) · admin 看 systemHealth 7 天错误不全 0
  const auditDemo = [
    { actor: 'GM01', role: 'gm', module: 'auth', action: 'login', sev: 'info', title: 'GM01 登录成功' },
    { actor: 'FIN-01', role: 'finance', module: 'auth', action: 'login', sev: 'info', title: 'FIN-01 登录成功' },
    { actor: 'SALES-01', role: 'sales', module: 'auth', action: 'login', sev: 'info', title: 'SALES-01 登录成功' },
    { actor: 'WH-02', role: 'warehouse', module: 'auth', action: 'login', sev: 'info', title: 'WH-02 登录成功' },
    { actor: 'WH-03', role: 'warehouse', module: 'auth', action: 'login', sev: 'info', title: 'WH-03 登录成功' },
    { actor: 'SALES-01', role: 'sales', module: 'sales', action: 'so_created', sev: 'info', title: '创建 SO-2026-0001' },
    { actor: 'SUP-01', role: 'supervisor', module: 'production', action: 'wo_started', sev: 'info', title: '工单 WO-2026-0001 开工' },
    { actor: 'QC-01', role: 'quality', module: 'quality', action: 'qc_pass', sev: 'info', title: 'IPQC 检验合格' },
    { actor: 'WH-02', role: 'warehouse', module: 'inventory', action: 'material_received', sev: 'info', title: '收料入库 K纸 2000 卷' },
    { actor: 'FIN-01', role: 'finance', module: 'finance', action: 'payment_received', sev: 'info', title: '收款 đ552M' },
  ];
  auditDemo.forEach((e, i) => {
    const ts = new Date(Date.now() - (i * 3600000)).toISOString();
    db.run(`INSERT INTO audit_events (actor_role, module, action, severity, title, created_at) VALUES (?, ?, ?, ?, ?, ?)`,
      [e.role, e.module, e.action, e.sev, `[${e.actor}] ${e.title}`, ts]);
  });
  // 5 工单 (关联 SO 1-5)
  for (let i = 1; i <= 5; i++) {
    const dueDate = new Date(Date.now() + (i * 3 + 7) * 86400000).toISOString().slice(0, 10);
    db.run(`INSERT INTO work_orders (code, so_id, product_id, planned_qty, completed_qty, status, urgency, due_date) VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
      [`WO-2026-${String(i).padStart(4, '0')}`, i, i, 5000, i === 1 ? 1200 : i === 2 ? 3500 : 0,
       i === 1 ? 'in_progress' : i === 2 ? 'in_progress' : 'planned',
       i === 4 ? 'urgent' : 'medium', dueDate]);
  }
  // 3 AR 发票 (开了未付)
  for (let i = 1; i <= 3; i++) {
    db.run(`INSERT INTO customer_invoices (code, customer_id, total, paid_amount, status) VALUES (?, ?, ?, ?, ?)`,
      [`INV-AR-2026${String(i).padStart(4, '0')}`, i, 50000000 + i * 10000000, i === 1 ? 20000000 : 0,
       i === 1 ? 'partial' : 'unpaid']);
  }
  // 9 车辆 (v18.9.46 真实车队)
  const vehicles = [
    { code: 'VH-001', plate: '61C-12345', type: '5吨货车', cap: 5 },
    { code: 'VH-002', plate: '61C-22345', type: '5吨货车', cap: 5 },
    { code: 'VH-003', plate: '61C-32345', type: '10吨货车', cap: 10 },
    { code: 'VH-004', plate: '61C-42345', type: '10吨货车', cap: 10 },
    { code: 'VH-005', plate: '61C-52345', type: '15吨货车', cap: 15 },
    { code: 'VH-006', plate: '61C-62345', type: '15吨货车', cap: 15 },
    { code: 'VH-007', plate: '61C-72345', type: '20吨货车', cap: 20 },
    { code: 'VH-008', plate: '61C-82345', type: '集装箱车', cap: 25 },
    { code: 'VH-009', plate: '61C-92345', type: '集装箱车', cap: 25 },
  ];
  vehicles.forEach(v => {
    db.run(`INSERT INTO vehicles (vehicle_code, plate_number, vehicle_type, capacity_ton, status) VALUES (?, ?, ?, ?, 'available')`,
      [v.code, v.plate, v.type, v.cap]);
  });
  // 5 成品库存 (WO 完工后入库)
  for (let i = 1; i <= 5; i++) {
    db.run(`INSERT INTO inventory (product_id, qty, unit_price, batch, inbound_date, status) VALUES (?, ?, ?, ?, ?, 'in_stock')`,
      [i, 2000 + i * 500, 25000, `BATCH-2026-${String(i).padStart(4, '0')}`, now]);
  }
  // 3 收料单 (今日 2 + 昨日 1)
  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  db.run(`INSERT INTO material_receipts (code, po_id, supplier_id, material_type, material_id, qty, qty_received, receipt_date, iqc_status) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    ['MR-2026-0001', 1, 1, 'raw', 1, 2000, 2000, today, 'pending']);
  db.run(`INSERT INTO material_receipts (code, po_id, supplier_id, material_type, material_id, qty, qty_received, receipt_date, iqc_status) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    ['MR-2026-0002', 2, 2, 'raw', 4, 5000, 4950, today, 'passed']);
  db.run(`INSERT INTO material_receipts (code, po_id, supplier_id, material_type, material_id, qty, qty_received, receipt_date, iqc_status) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    ['MR-2026-0003', 3, 3, 'aux', 1, 200, 200, yesterday, 'passed']);
  // 2 发货单 (今日 1 + 在途 1)
  db.run(`INSERT INTO shipping_notes (code, so_id, customer_id, vehicle_id, ship_date, status, total_qty, total_value) VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
    ['DL-2026-0001', 21, 1, 1, today, 'planned', 2000, 50000000]);
  db.run(`INSERT INTO shipping_notes (code, so_id, customer_id, vehicle_id, ship_date, status, total_qty, total_value) VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
    ['DL-2026-0002', 22, 2, 3, yesterday, 'shipped', 2500, 60000000]);

  // ⭐ v348 · 5 contracts SEED (5 customers 各 1 合同 · timeline 节点 2 绿色)
  for (let i = 1; i <= 5; i++) {
    const total = 500000000 + i * 50000000; // 0.5B-0.75B
    const signedDate = new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10); // 90 天前签
    const validUntil = new Date(Date.now() + 275 * 86400000).toISOString().slice(0, 10); // 一年期
    try {
      db.run(`INSERT INTO contracts (code, customer_id, contract_type, total_amount, signed_date, valid_until, status) VALUES (?, ?, ?, ?, ?, ?, ?)`,
        [`CONT-2026-${String(i).padStart(4, '0')}`, i, '年度框架合同', total, signedDate, validUntil, 'active']);
    } catch {}
  }

  // ⭐ v347 · shipped SO (20-27) 完整闭环 SEED · timeline 4-9 节点真数据
  // 8 shipped SO (id 20-27) · 每个建 WO(completed) + 1 work_report + 1 qc_records + 1 inventory(wo_id) + 1 shipping_notes + 1 customer_invoice + 1 payment
  for (let soId = 20; soId <= 27; soId++) {
    const woCode = `WO-2026-${String(soId).padStart(4, '0')}`;
    const planned = 2000 + soId * 100; // 4000-4700
    const productId = (soId % 5) + 1;
    const custId = (soId % 5) + 1;
    const total = 100000000 + soId * 1000000; // 一致 SO total
    const shipDate = new Date(Date.now() - (28 - soId) * 86400000).toISOString().slice(0, 10);

    // 1. WO completed (节点 5)
    try {
      db.run(`INSERT INTO work_orders (code, so_id, product_id, planned_qty, completed_qty, status, urgency, due_date) VALUES (?, ?, ?, ?, ?, 'completed', 'medium', ?)`,
        [woCode, soId, productId, planned, planned, shipDate]);
    } catch {}
    // get wo_id (last inserted)
    const woRow = db.exec(`SELECT id FROM work_orders WHERE code='${woCode}'`)[0];
    const woId = woRow?.values?.[0]?.[0] as number;
    if (!woId) continue;

    // 2. work_report (节点 6)
    try {
      db.run(`INSERT INTO work_reports (work_order_id, worker_code, qty_good, report_time, created_at) VALUES (?, 'W-01', ?, ?, ?)`,
        [woId, planned, shipDate + ' 16:00', shipDate + ' 16:00']);
    } catch {}

    // 3. qc_records (节点 7 · 合格)
    try {
      db.run(`INSERT INTO qc_records (work_order_id, result, created_at) VALUES (?, '合格', ?)`,
        [woId, shipDate + ' 17:00']);
    } catch {}

    // 4. inventory (节点 8 · 入库)
    try {
      db.run(`INSERT INTO inventory (product_id, qty, unit_price, batch, inbound_date, status, wo_id) VALUES (?, ?, 25000, ?, ?, 'in_stock', ?)`,
        [productId, planned, `BATCH-${woCode}`, shipDate, woId]);
    } catch {}

    // 5. shipping_notes (节点 9 · 已发货, 跳过 21 + 22 已有)
    if (soId !== 21 && soId !== 22) {
      try {
        db.run(`INSERT INTO shipping_notes (code, so_id, customer_id, vehicle_id, ship_date, status, total_qty, total_value) VALUES (?, ?, ?, ?, ?, 'delivered', ?, ?)`,
          [`DL-2026-${String(soId - 17).padStart(4, '0')}`, soId, custId, (soId % 9) + 1, shipDate, planned, total]);
      } catch {}
    }

    // 6. customer_invoices (节点 10 · AR 开票)
    try {
      const arPaid = soId <= 22 ? 0 : Math.round(total * 0.5); // 22 后已收 50%
      db.run(`INSERT INTO customer_invoices (code, customer_id, total, paid_amount, status) VALUES (?, ?, ?, ?, ?)`,
        [`INV-AR-2026${String(soId).padStart(4, '0')}`, custId, total, arPaid,
          arPaid >= total ? 'paid' : arPaid > 0 ? 'partial' : 'unpaid']);
    } catch {}

    // 7. customer_payments (节点 11 · 收款 · 仅 23-27)
    if (soId >= 23) {
      try {
        db.run(`INSERT INTO customer_payments (ref, customer_id, amount, payment_date) VALUES (?, ?, ?, ?)`,
          [`PAY-AR${String(soId).padStart(4, '0')}`, custId, Math.round(total * 0.5), shipDate]);
      } catch {}
    }
  }

  console.log('[seed] business demo data seeded · v347 含 8 shipped SO 完整闭环 (WO+report+QC+inventory+shipping+AR+收款)');
}

// ============================================================
// PBKDF2 (R14 严守 · 复用 auth.ts 设计)
// ============================================================
async function hashPassword(password: string): Promise<{ salt: string; hash: string }> {
  const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES));
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw', enc.encode(password), 'PBKDF2', false, ['deriveBits']
  );
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt, iterations: PBKDF2_ITER, hash: 'SHA-256' },
    keyMaterial, HASH_BITS
  );
  const hash = new Uint8Array(bits);
  return {
    salt: bytesToHex(salt),
    hash: bytesToHex(hash),
  };
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
}
